/**
 * ROLE-LENS-DESIGN-SYSTEM-V1 -- StatusChip: THE ONE status-canon render map.
 *
 * One home for status-token -> (label, tone) so no screen ever re-invents
 * status wording or chip color again (design-language 03 P3 consistency law:
 * the SAME state shows the SAME label and hue everywhere). Renders through
 * the existing AcBadge -- this is a CANON + a thin delegate, never a second
 * chip visual, never a competitor to AcBadge (which stays the tone/pill
 * owner). The former screen-local GrowthSpinePage StatusChip is retired (OWNER-OPERATING-SYSTEM-V1)
 * (legacy `.status-chip` classes; untouched, folds onto this canon in a
 * later adoption package).
 *
 * RECEPTION-OS-BUILD-V1 (F-09): the canon DATA + pure resolvers moved to the
 * sibling `status-canon.ts` (the repo's pure-view idiom) so pure modules can
 * consume the canon without importing a component. Everything is re-exported
 * here, so this file REMAINS the one import home; the pair is ONE canon.
 * Adopters landed by that package: ClinicDayBoard.dayStatusChip,
 * owner/Lead360Panel, owner/leakage-funnel-view (fulfilled bucket), and
 * patients/patient-360-view (its APPOINTMENT_STATUS_LABELS source copy is
 * retired -- the extraction-register note below is thereby EXECUTED for the
 * appointment domain).
 *
 * SOURCE SCREENS (extraction register; label strings are verbatim copies,
 * lockstep-pinned in role-lens-primitives.test.tsx so they cannot drift
 * while both homes exist -- adoption packages later retire the per-screen
 * copies):
 * - appointment labels: patients/patient-360-view.ts
 *   APPOINTMENT_STATUS_LABELS; tones: patients/PatientRecordPage.tsx
 *   STATUS_TONE (Patient 360 -- WALK 21 PASSED 2026-07-15; golden source).
 *   ADOPTED + RETIRED by RECEPTION-OS-BUILD-V1: patient-360-view now
 *   delegates to this canon.
 * - visit / conversion / payment / identity / sla labels:
 *   record/current-truth.ts exported label constants ("the ONE vocabulary
 *   both surfaces render"); conversion / payment / sla / identity /
 *   follow-up tones: record/RecordHeaderBand.tsx CurrentStateBand
 *   (Lead 360 -- record band PROVEN live 2026-07-11, WALK 5 + WALK 6 #252;
 *   golden source. WALK 15 retrofit steps remain LIVE-RUN PENDING).
 * - commercial labels: patients/patient-360-view.ts
 *   CONVERSION_STATE_LABELS (the #267 payment-derived commercial canon;
 *   WALK 21 PASSED). No founder-walked TONE precedent exists for the
 *   commercial states -- they render neutral pending a design ruling
 *   (IMPLEMENTATION REFERENCE, UNVALIDATED for tone).
 * - journey labels + tones: patients/patient-360-view.ts
 *   JOURNEY_STATE_LABELS + patients/PatientRecordPage.tsx JOURNEY_TONE
 *   (WALK 18 LIVE-RUN PENDING -- IMPLEMENTATION REFERENCE, UNVALIDATED).
 * - intake / clinical_operational labels: patients/patient-360-view.ts
 *   INTAKE_STATUS_LABELS + CLINICAL_STATE_LABELS (operational state ONLY,
 *   never clinical content); no tone precedent -> neutral.
 * - stage: the CRM stage chip is ALWAYS neutral with an explicit micro-label
 *   (RecordHeaderBand STAGE DISPLAY RULE); stage LABEL text stays owned by
 *   the lifecycle rail (railStageLabel, equivalence-pinned to the backend
 *   engine) -- callers pass the resolved label; this canon deliberately
 *   mints NO third stage-label copy.
 *
 * Contextual statuses (a label that carries record-specific content, e.g.
 * "Collect Rs X" or the linked patient's UMR) are CALLER-RESOLVED: pass the
 * resolved string via the `label` prop; the canon still owns the tone. An
 * unknown status token renders neutral with a readable de-cased fallback --
 * honest, never a raw token echo, never an invented meaning.
 *
 * CLINICAL WIRE LAW (CLAUDE.md "Clinical visibility"): presentation only,
 * never the security boundary -- the canon maps ADMINISTRATIVE state tokens
 * that already arrived on a lawful wire; server SELECT fences remain the
 * authority. Status labels are deliberately audience-NEUTRAL (P3: the same
 * status must read identically for every role); audience shaping belongs to
 * the primitives that decide WHETHER a facet renders, never to WHAT a
 * status is called.
 */

import AcBadge from './AcBadge';
import { statusChipLabel, statusChipTone } from './status-canon';
import type { StatusChipDomain } from './status-canon';

export {
  STATUS_CANON,
  appointmentStatusDisplayLabel,
  appointmentStatusDisplayTone,
  statusChipLabel,
  statusChipTone,
  statusFallbackLabel,
} from './status-canon';
export type {
  AppointmentStatusLabelOverrides,
  StatusChipDomain,
} from './status-canon';

export interface StatusChipProps {
  readonly domain: StatusChipDomain;
  /** The wire status token (canonical vocabulary per domain). */
  readonly status: string;
  /** Caller-resolved label for contextual statuses (see header JSDoc). */
  readonly label?: string | null;
  readonly title?: string;
  readonly testId?: string;
}

export default function StatusChip({
  domain,
  status,
  label,
  title,
  testId,
}: StatusChipProps): JSX.Element {
  return (
    <span
      className="ac-statuschip"
      data-domain={domain}
      data-status={status}
      data-testid={testId}
    >
      <AcBadge tone={statusChipTone(domain, status)} title={title}>
        {statusChipLabel(domain, status, label)}
      </AcBadge>
    </span>
  );
}
